import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { SIZE_COLORS, SIZES } from '../data/constants';

import logger from '../utils/logger.js';
const SizesSection = ({ 
  sizes, 
  setSizes, 
  listingData, 
  extractedVariations, 
  onSave,
  title = "Description - Sizes"
}) => {
  logger.log('SizesSection props:', {
    sizes,
    listingData: listingData?.title,
    extractedVariations,
    availableSizes: extractedVariations?.sizes,
    imagesData: listingData?.images_data
  });
  const handleSizeChange = (index, field, value) => {
    const newSizes = [...sizes];
    newSizes[index][field] = value;
    setSizes(newSizes);
  };

  const handleSizeToggle = (index) => {
    const newSizes = [...sizes];
    newSizes[index].enabled = !newSizes[index].enabled;
    setSizes(newSizes);
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave();
    }
  };

  return (
    <Accordion defaultExpanded={true}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Typography variant="h6">
            {title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
              TRUEscale Sizes
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSave}
              sx={{ 
                fontSize: '0.8rem',
                px: 2,
                py: 0.5
              }}
            >
              Save Sizes
            </Button>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: 2 
        }}>
          {sizes.map((size, index) => {
            // Find the correct image for this size
            let sizeImage = null;
            
            // First try: Use official variation images mapping if available
            if (listingData?.variation_images_data?.results && listingData.variation_images_data.results.length > 0) {
              const sizeVariation = listingData.variation_images_data.results.find(v => 
                v.value && v.value.toUpperCase() === size.name.toUpperCase()
              );
              
              if (sizeVariation) {
                const image = listingData.images_data.find(img => 
                  img.listing_image_id === sizeVariation.image_id
                );
                if (image) {
                  sizeImage = image;
                }
              }
            }
            
            // Second try: Use the listing's actual available_sizes array for proper mapping
            if (!sizeImage && listingData?.images_data && extractedVariations?.sizes) {
              const availableSizes = extractedVariations.sizes;
              const sizeIndex = availableSizes.indexOf(size.name);
              
              if (sizeIndex >= 0) {
                // Map available sizes to images: first available size = image1, second = image2, etc.
                // Skip the first image (overall view) and start mapping from second image
                const imageIndex = sizeIndex + 1;
                if (imageIndex < listingData.images_data.length) {
                  sizeImage = listingData.images_data[imageIndex];
                }
              }
            }
            
            // Third try: Fallback mapping based on size order when no variation data exists
            if (!sizeImage && listingData?.images_data && listingData.images_data.length > 1) {
              // For listings without variation mappings, map sizes to images in order
              // Use intelligent size ordering: NANO, MICRO, SMALL, MEDIUM, LARGE, XLARGE
              const sizeOrder = ['NANO', 'MICRO', 'SMALL', 'MEDIUM', 'LARGE', 'XLARGE'];
              const sizeIndex = sizeOrder.indexOf(size.name.toUpperCase());
              
              if (sizeIndex >= 0) {
                // Skip the first image (overall view) and map sizes to subsequent images
                const imageIndex = sizeIndex + 1;
                if (imageIndex < listingData.images_data.length) {
                  sizeImage = listingData.images_data[imageIndex];
                }
              }
            }
            
            // Final fallback: if no specific mapping, use the first image
            if (!sizeImage && listingData?.images_data?.length > 0) {
              sizeImage = listingData.images_data[0];
            }
            
            return (
              <Box key={size.name} sx={{ 
                p: 2, 
                border: 1, 
                borderColor: size.enabled ? SIZE_COLORS[size.name] : 'rgba(255,255,255,0.2)', 
                borderRadius: 1,
                bgcolor: size.enabled ? SIZE_COLORS[size.name] : 'transparent',
                position: 'relative'
              }}>
                {/* Header with checkbox and size name */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Checkbox
                    size="small"
                    checked={size.enabled}
                    onChange={() => handleSizeToggle(index)}
                    sx={{
                      color: size.enabled ? '#ffffff' : SIZE_COLORS[size.name],
                      '&.Mui-checked': { 
                        color: size.enabled ? '#ffffff' : SIZE_COLORS[size.name] 
                      }
                    }}
                  />
                  <Typography variant="subtitle1" sx={{ 
                    color: size.enabled ? '#ffffff' : SIZE_COLORS[size.name], 
                    fontWeight: 'bold',
                    flex: 1
                  }}>
                    {size.name}
                  </Typography>
                </Box>
                
                {/* Size thumbnail and form fields side by side */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  {/* Square thumbnail on the left */}
                  <Box sx={{ 
                    width: 60, 
                    height: 60, 
                    overflow: 'hidden',
                    border: 1,
                    borderColor: 'rgba(255,255,255,0.2)',
                    flexShrink: 0
                  }}>
                    {sizeImage ? (
                      <Box
                        component="img"
                        src={sizeImage.url_570xN || sizeImage.url_fullxfull}
                        alt={`${size.name} size`}
                        sx={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <Box sx={{ 
                        width: '100%', 
                        height: '100%', 
                        bgcolor: 'rgba(255,255,255,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography variant="caption" color="text.secondary">
                          No image
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Form fields on the right */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1 }}>
                    <TextField
                      size="small"
                      label="Width"
                      placeholder="Width"
                      variant="outlined"
                      inputProps={{
                        maxLength: 6,
                        pattern: '[0-9.]*',
                        inputMode: 'decimal'
                      }}
                      sx={{ flex: 1, minWidth: '80px' }}
                      disabled={!size.enabled}
                      value={size.width}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        handleSizeChange(index, 'width', value);
                      }}
                    />
                    <TextField
                      size="small"
                      label="Height"
                      placeholder="Height"
                      variant="outlined"
                      inputProps={{
                        maxLength: 6,
                        pattern: '[0-9.]*',
                        inputMode: 'decimal'
                      }}
                      sx={{ flex: 1, minWidth: '80px' }}
                      disabled={!size.enabled}
                      value={size.height}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        handleSizeChange(index, 'height', value);
                      }}
                    />
                    <TextField
                      size="small"
                      label="Price"
                      placeholder="Price"
                      variant="outlined"
                      inputProps={{
                        maxLength: 6,
                        pattern: '[0-9.]*',
                        inputMode: 'decimal'
                      }}
                      InputProps={{
                        startAdornment: <Typography variant="body2" sx={{ color: 'text.secondary' }}>$</Typography>,
                      }}
                      sx={{ flex: 1, minWidth: '80px' }}
                      disabled={!size.enabled}
                      value={size.price}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        handleSizeChange(index, 'price', value);
                      }}
                    />
                    <TextField
                      size="small"
                      label="SKU"
                      placeholder="SKU"
                      variant="outlined"
                      sx={{ flex: 1.5, minWidth: '120px' }}
                      disabled={!size.enabled}
                      value={size.sku}
                      onChange={(e) => handleSizeChange(index, 'sku', e.target.value)}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

SizesSection.propTypes = {
  sizes: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    enabled: PropTypes.bool,
    packCount: PropTypes.number
  })).isRequired,
  setSizes: PropTypes.func.isRequired,
  listingData: PropTypes.object,
  extractedVariations: PropTypes.object,
  onSave: PropTypes.func,
  title: PropTypes.string
};

export default SizesSection;
